import * as React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Form } from '@/components/forms/Form';
import { FormInput } from '@/components/forms/FormInput';
import { FormSelect } from '@/components/forms/FormSelect';
import { FormSwitch } from '@/components/forms/FormSwitch';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api-client';
import { errorMessage } from '@/lib/errors';
import { toast } from '@/components/ui/toast';
import { bankLabel } from '@/lib/bank-label';
import { looksMasked } from '@b1fr0st/shared/pii/mask';

interface BankMaster {
  code: string;
  name: string;
  localName?: string | null;
  country?: string | null;
}

// Mirrors svc-member BankCreateBody.
const Schema = z.object({
  bankCode: z.string().min(1).max(16),
  accountNumber: z.string().min(3).max(32),
  accountName: z.string().min(1).max(255),
  isDefault: z.boolean(),
});

/**
 * The same shape, for an account whose NUMBER the operator may not read.
 *
 * svc-member answers a caller without `member:reveal_pii` with `••••7890`, and
 * this dialog used to seed the field from it and PATCH it straight back — which
 * replaced the account a withdrawal is paid into with a row of dots. The field
 * is seeded BLANK instead, and blank means "keep the stored number", so the
 * create-side minimum cannot apply to it.
 */
const KeepNumberSchema = Schema.extend({
  accountNumber: z.union([z.literal(''), z.string().min(3).max(32)]),
});

type Values = z.infer<typeof Schema>;

const DEFAULTS: Values = { bankCode: '', accountNumber: '', accountName: '', isDefault: false };

/**
 * The PATCH body for an edit: only what the operator actually changed.
 *
 * Sending every field back is how a masked account number got written over a
 * real one, and it also turns a colleague's concurrent edit into a silent
 * revert. `dirty` is react-hook-form's `dirtyFields`, compared against the
 * values `reset()` seeded — so a masked number, seeded blank, travels only once
 * a real one has been typed over it.
 */
function editPatch(
  values: Values,
  dirty: Partial<Record<keyof Values, boolean>>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const k of ['bankCode', 'accountNumber', 'accountName', 'isDefault'] as const) {
    if (dirty[k]) out[k] = values[k];
  }
  return out;
}

export interface EditingBankAccount {
  id: string;
  bankCode: string;
  accountNumber: string;
  accountName: string;
  isDefault?: boolean;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  memberId: string;
  /** When set, the dialog edits this existing account (PATCH) instead of creating a new one (POST). */
  editing?: EditingBankAccount | null;
}

export function MemberBankFormDialog({ open, onOpenChange, memberId, editing }: Props) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();

  const banksQuery = useQuery<{ items: BankMaster[] }>({
    queryKey: ['banks-master'],
    queryFn: () => api.get('/api/v1/banks'),
    staleTime: 5 * 60 * 1000,
  });

  // ponytail: flat dropdown of ~100 banks; extract the searchable BankPicker from
  // finance/BankAccountFormDialog if the flat list proves unwieldy.
  const bankOptions = React.useMemo(
    () => (banksQuery.data?.items ?? []).map((b) => ({ value: b.code, label: bankLabel(b, i18n.language) })),
    [banksQuery.data, i18n.language],
  );

  /** The stored number came back masked, so it is a placeholder, never a value. */
  const maskedNumber = editing && looksMasked(editing.accountNumber) ? editing.accountNumber : null;

  const form = useForm<Values>({
    resolver: zodResolver(maskedNumber ? KeepNumberSchema : Schema),
    defaultValues: DEFAULTS,
  });

  React.useEffect(() => {
    if (!open) return;
    form.reset(
      editing
        ? {
            bankCode: editing.bankCode,
            accountNumber: maskedNumber ? '' : editing.accountNumber,
            accountName: editing.accountName,
            isDefault: editing.isDefault ?? false,
          }
        : DEFAULTS,
    );
  }, [open, editing, maskedNumber, form]);

  const mutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      editing
        ? api.patch(`/api/v1/members/${memberId}/bank-accounts/${editing.id}`, payload)
        : api.post(`/api/v1/members/${memberId}/bank-accounts`, payload),
    onSuccess: () => {
      toast.success(editing ? t('members.bankUpdated') : t('members.bankAdded'));
      qc.invalidateQueries({ queryKey: ['/api/v1/members', memberId] });
      onOpenChange(false);
    },
    onError: (err) => {
      toast.error(errorMessage(err, t('common.error')));
    },
  });

  const onSubmit = form.handleSubmit((values) => {
    if (!editing) {
      mutation.mutate({ ...values });
      return;
    }
    const patch = editPatch(values, form.formState.dirtyFields);
    // An edit that changed nothing is a close, not a request: the PATCH route
    // answers an empty body with 404, and an operator who opened the dialog to
    // look has not asked for anything to happen.
    if (Object.keys(patch).length === 0) {
      onOpenChange(false);
      return;
    }
    mutation.mutate(patch);
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{editing ? t('finance.editBankAccount') : t('finance.addBankAccount')}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={onSubmit} className="space-y-3">
            <FormSelect<Values> name="bankCode" label={t('finance.bank')} options={bankOptions} />
            <FormInput<Values>
              name="accountNumber"
              label={t('finance.accountNumber')}
              {...(maskedNumber
                ? { placeholder: maskedNumber, description: t('members.maskedFieldHint') }
                : {})}
            />
            <FormInput<Values> name="accountName" label={t('finance.accountName')} />
            <FormSwitch<Values> name="isDefault" label={t('members.primaryBank')} />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={mutation.isPending}>
                {editing ? t('common.save') : t('common.create')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
